/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
import {
  getSessionToken,
  saveSessionToken,
  saveFileDownloadToken,
  endSession,
} from "./shared/session";

export default class APIClient {
  // NOTE: jjw: wrappers now return promises, so any future calls should attach
  // success/failure callbacks using .then() / .catch() instead of passing args

  static get<T>(path: string, success = null, failure = null): Promise<T> {
    let promise = this.makeRequest<T>("GET", path, null);

    if (success) {
      promise = promise.then(success);
    }
    if (failure) {
      promise = promise.catch(failure);
    }

    return promise;
  }

  // delete is a reserved word
  static deleteRequest<T>(path: string, success = null, failure = null): Promise<T> {
    let promise = this.makeRequest<T>("DELETE", path, null);

    if (success) {
      promise = promise.then(success);
    }
    if (failure) {
      promise = promise.catch(failure);
    }

    return promise;
  }

  public static post<T>(path: string, data: Object, success = null, failure = null): Promise<T> {
    let promise = this.makeRequest<T>("POST", path, data);

    if (success) {
      promise = promise.then(success);
    }
    if (failure) {
      promise = promise.catch(failure);
    }

    return promise;
  }

  public static put<T>(path: string, data: Object, success = null, failure = null): Promise<T> {
    let promise = this.makeRequest<T>("PUT", path, data);

    if (success) {
      promise = promise.then(success);
    }
    if (failure) {
      promise = promise.catch(failure);
    }

    return promise;
  }

  public static patch<T>(path: string, data: Object, success = null, failure = null): Promise<T> {
    let promise = this.makeRequest<T>("PATCH", path, data);

    if (success) {
      promise = promise.then(success);
    }
    if (failure) {
      promise = promise.catch(failure);
    }

    return promise;
  }

  public static upload<T>(path: string, target: any, success = null, failure = null): Promise<T> {
    const data = new FormData();
    data.append(target.name, target.files[0], target.files[0].name);
    let promise = this.makeRequest<T>("POST", path, data);

    if (success) {
      promise = promise.then(success);
    }
    if (failure) {
      promise = promise.catch(failure);
    }

    return promise;
  }

  public static dropzoneUpload(
    path: string,
    name: string,
    file: any,
    additionalData: Object,
    success = null,
    failure = null,
  ) {
    const data = new FormData();
    data.append(name, file, file.name);
    for (const prop in additionalData) {
      data.append(prop, additionalData[prop]);
    }
    return this.makeRequest("POST", path, data).then(success).catch(failure);
  }

  private static makeRequest<T>(method: string, path: string, data: Object): Promise<T> {
    const headers: Headers = new Headers();
    headers.append("Accept", "application/json");
    if (getSessionToken()) {
      headers.append("Eave-Authorization", `Bearer ${getSessionToken()}`);
    }
    if (!this.isUpload(data)) {
      headers.append("Content-Type", "application/json");
    }

    const options: RequestInit = {
      method,
      headers,
      credentials: "same-origin",
    };

    if (method === "POST" || method === "PUT" || method === "PATCH") {
      options.body = this.isUpload(data) ? <FormData>data : JSON.stringify(data);
    }

    // fetch only rejects on actual network errors (404s, etc., are fulfilled);
    // to handle this, wrap in second promise and fulfill/reject it manually

    return new Promise((resolve, reject) => {
      fetch(`/api/v1${path}`, options)
        .then((resp: Response) => {
          if (resp.status === 403) {
            endSession();
            if (!window.location.pathname.includes("/login")) {
              window.location.pathname = "/operators/login";
            }
            return;
          }

          if (resp.ok) {
            const authToken = resp.headers.get("Eave-Authorization");
            if (authToken) {
              saveSessionToken(authToken);
            }
            const fileAuthToken = resp.headers.get("Eave-File-Authorization");
            if (fileAuthToken) {
              saveFileDownloadToken(fileAuthToken);
            }
            resp.json().then(json => {
              resolve(json.response);
            });
          } else if (resp.status === 502) {
            reject({ meta: { error_detail: "bad gateway" }, status: 502 });
          } else if (resp.status === 503) {
            reject({ meta: { error_detail: "service unavailable" }, status: 503 });
          } else if (resp.status === 504) {
            reject({ meta: { error_detail: "gateway timeout" }, status: 504 });
          } else {
            return resp.json().then(json => {
              json.meta = json.meta || {};
              json.meta.code = resp.status;
              reject(json);
            });
          }
        })
        .catch((err: Error) => {
          reject(err);
        });
    });
  }

  private static isUpload(data): boolean {
    return Object.prototype.toString.call(data) === "[object FormData]";
  }
}
